if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        // Allow product-info to register preprocess callbacks for variant swaps
        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        // Optional: ensure close button always closes
        this.querySelector('.quick-add-modal__toggle')?.addEventListener('click', () => this.hide());
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        const baseUrl = opener.getAttribute('data-product-url');
        if (!baseUrl) {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner')?.classList.add('hidden');
          return;
        }

        // ✅ Step B: fetch the dedicated quick-add view (minimal template)
        const quickAddUrl = (() => {
          try {
            const u = new URL(baseUrl, window.location.origin);
            u.searchParams.set('view', 'quick-add');
            return u.toString();
          } catch (e) {
            return baseUrl.includes('?') ? `${baseUrl}&view=quick-add` : `${baseUrl}?view=quick-add`;
          }
        })();

        const fetchProductView = (url) =>
          fetch(url, { credentials: 'same-origin' })
            .then((r) => r.text())
            .then((txt) => {
              const doc = new DOMParser().parseFromString(txt, 'text/html');
              const productElement = doc.querySelector('product-info');
              return { doc, productElement };
            });

        fetchProductView(quickAddUrl)
          // Fallback: if for any reason view template doesn’t return product-info, use full product page
          .then(({ doc, productElement }) => {
            if (productElement) return { doc, productElement };
            return fetchProductView(baseUrl);
          })
          .then(({ doc, productElement }) => {
            if (!productElement) throw new Error('Quick add: <product-info> not found in fetched HTML');

            // ✅ Critical: load the quick-add view’s CSS into the current page (once)
            this.ensureQuickAddStyles(doc);

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            window?.Shopify?.PaymentButton?.init?.();
            window?.ProductModel?.loadShopifyXR?.();

            super.show(opener);
          })
          .catch((e) => {
            console.error(e);
            // Worst case: go to product page rather than "empty modal"
            window.location.href = baseUrl;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      ensureQuickAddStyles(doc) {
        if (!doc) return;

        const REQUIRED = [
          'quick-add.css',
          'component-price.css',
          'component-product-variant-picker.css',
          'component-swatch.css',
          'component-swatch-input.css',
          'cbh-quick-add.css',
        ];

        const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"]')).filter((l) => {
          const href = l.getAttribute('href') || '';
          return REQUIRED.some((name) => href.includes(name));
        });

        if (!links.length) return;

        const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((l) => l.href);

        links.forEach((link) => {
          const href = link.getAttribute('href');
          if (!href) return;

          // normalise to absolute for comparison
          let absHref = href;
          try {
            absHref = new URL(href, window.location.origin).href;
          } catch (e) {}

          if (existing.includes(absHref)) return;

          const clone = link.cloneNode(true);
          document.head.appendChild(clone);
          existing.push(absHref);
        });
      }

      preprocessHTML(productElement) {
        // Keep colour scheme classes for modal wrapper
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });

        // ✅ MUST remove nested modals first (prevents global.js ModalDialog crash)
        this.removeDOMElements(productElement);

        // ✅ Safe ID de-dupe (never let this crash)
        try {
          this.preventDuplicatedIDs(productElement);
        } catch (e) {
          console.error('Quick add ID de-dupe failed:', e);
        }

        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.ensureSingleVariantIdInput(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelector('pickup-availability')?.remove();
        productElement.querySelector('product-modal')?.remove();
        productElement.querySelectorAll('modal-dialog').forEach((m) => m.remove());
      }

      preventDuplicatedIDs(productElement) {
        const originalSection = productElement.dataset.section;
        if (!originalSection) return;

        const newSection = `quickadd-${originalSection}`;
        productElement.dataset.originalSection = originalSection;
        productElement.dataset.section = newSection;

        const idMap = new Map();

        // ✅ Fix: getAttribute('id') => always a string (prevents SVGAnimatedString issues)
        productElement.querySelectorAll('[id]').forEach((el) => {
          const oldId = el.getAttribute('id');
          if (!oldId) return;

          const oldIdStr = String(oldId);
          let newId = oldIdStr;

          const token = String(originalSection);

          if (oldIdStr === token) {
            newId = newSection;
          } else if (oldIdStr.endsWith(`-${token}`)) {
            newId = oldIdStr.replace(`-${token}`, `-${newSection}`);
          } else if (oldIdStr.includes(`-${token}-`)) {
            newId = oldIdStr.split(`-${token}-`).join(`-${newSection}-`);
          }

          if (newId !== oldIdStr) {
            idMap.set(oldIdStr, newId);
            el.setAttribute('id', newId);
          }
        });

        const refAttrs = ['for', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'form'];
        productElement.querySelectorAll(refAttrs.map((a) => `[${a}]`).join(',')).forEach((el) => {
          refAttrs.forEach((attr) => {
            const v = el.getAttribute(attr);
            if (!v) return;

            const parts = v.split(' ').filter(Boolean);
            const next = parts.map((p) => idMap.get(p) || p).join(' ');
            if (next !== v) el.setAttribute(attr, next);
          });
        });

        productElement.querySelectorAll('a[href^="#"]').forEach((a) => {
          const href = a.getAttribute('href');
          if (!href || href.length < 2) return;
          const raw = href.slice(1);
          const mapped = idMap.get(raw);
          if (mapped) a.setAttribute('href', `#${mapped}`);
        });

        const dataAttrs = ['data-section', 'data-section-id'];
        productElement.querySelectorAll(dataAttrs.map((a) => `[${a}]`).join(',')).forEach((el) => {
          dataAttrs.forEach((attr) => {
            const v = el.getAttribute(attr);
            if (!v) return;
            if (v === originalSection) el.setAttribute(attr, newSection);
            else if (v.includes(originalSection)) el.setAttribute(attr, v.split(originalSection).join(newSection));
          });
        });
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      ensureSingleVariantIdInput(productElement) {
        const forms = productElement.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
        if (!forms.length) return;

        forms.forEach((form) => {
          const inputs = Array.from(form.querySelectorAll('input[name="id"]'));
          let keeper = inputs[0];

          if (!keeper) {
            keeper = document.createElement('input');
            keeper.type = 'hidden';
            keeper.name = 'id';
            form.prepend(keeper);
          }

          inputs.slice(1).forEach((i) => i.remove());
          keeper.disabled = false;
        });
      }
    }
  );
}
